import { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  ArrowDownRight, ArrowLeftRight, ArrowUpRight, Ban, ChevronRight, FilePen, Gift, MoonStar, PencilLine,
  Printer, Wallet, XCircle,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Segmented } from "@/components/ui/segmented";
import { inr } from "@/lib/format";

/**
 * The owner dashboard's panels (spec:
 * docs/superpowers/specs/2026-10-25-owner-dashboard-design.md; owner 2026-10-25:
 * "can be much better, what you made looks very cheap").
 *
 * One read order: the day's takings first (one strip, payment split under it),
 * then how the day moved, then where money leaked, what sold and what went out.
 * Tokens only (DESIGN.md §2): hairline borders, no card shadows, colour spent on
 * meaning — green up, red down and loss, blue for the selected and for bars.
 */

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/** A token as a colour string. SVG presentation attributes do not resolve
 *  `hsl(var(--x))`, so chart fills read the variable's value from the page. */
export function tokenColor(name, fallback = "220 14% 46%") {
  if (typeof window === "undefined") return `hsl(${fallback})`;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim();
  return `hsl(${raw || fallback})`;
}

export function Panel({ title, aside, testId, error, loading = false, children, className = "", bodyClass = "p-loose" }) {
  return (
    <section className={`rounded-lg border bg-card flex flex-col min-w-0 ${className}`} data-testid={testId}>
      <header className="flex items-center justify-between gap-snug px-loose pt-base">
        <h2 className="text-lede font-semibold">{title}</h2>
        {aside}
      </header>
      {error && (
        <p className="mx-loose mt-snug rounded-md bg-danger-subtle px-snug py-tight text-meta text-danger" role="alert">
          Owner dashboard not updated — {error}
        </p>
      )}
      <div className={bodyClass}>{loading && !error ? <PanelSkeleton /> : children}</div>
    </section>
  );
}

function PanelSkeleton() {
  return (
    <div className="space-y-snug" aria-label="Loading">
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  );
}

function Delta({ now, before, money = false }) {
  if (!before) return <span className="text-meta text-muted-foreground">No earlier period to compare</span>;
  const change = ((now - before) * 100) / before;
  const up = change >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="flex flex-wrap items-center gap-x-tight gap-y-hair text-meta">
      <span className={`inline-flex shrink-0 items-center gap-hair self-start rounded-md px-tight py-hair font-medium tabular-nums ${up ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`}>
        <Icon className="h-3 w-3" aria-hidden />
        {Math.abs(change).toFixed(1)}%
      </span>
      <span className="text-muted-foreground tabular-nums">from {money ? inr(before) : before}</span>
    </span>
  );
}

const TYPE_LABEL = { dine_in: "Dine in", parcel: "Parcel", takeaway: "Takeaway", delivery: "Delivery" };
const typeLabel = t => TYPE_LABEL[t] || String(t || "").replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase());

// Payment modes take the palette in order; status colours are kept for status.
const MODE_TOKENS = ["primary", "chart-2", "chart-4", "chart-5", "chart-3", "chart-1"];

// ---------------------------------------------------------------------------
// 1. The day's takings
// ---------------------------------------------------------------------------

function Kpi({ label, value, testId, children }) {
  return (
    <div className="min-w-0 px-loose py-base">
      <div className="text-meta font-medium text-muted-foreground">{label}</div>
      {/* A headline figure is never cut: on a 360px phone `truncate` showed
          "Rs.79,231…." (2026-10-25). Smaller on phones; wraps before it hides. */}
      <div className="text-title sm:text-display font-bold tabular-nums [overflow-wrap:anywhere] mt-hair" data-testid={testId}>{value}</div>
      <div className="mt-tight min-h-[20px]">{children}</div>
    </div>
  );
}

export function TakingsStrip({ data, error }) {
  const s = data || {};
  const payments = useMemo(() => s.payments || [], [s.payments]);
  const colors = useMemo(() => payments.map((_p, i) => tokenColor(MODE_TOKENS[i % MODE_TOKENS.length])), [payments]);
  if (error || !data) {
    return (
      <Panel title="Takings" testId="dash-sales" error={error} loading={!data}>
        {null}
      </Panel>
    );
  }
  return (
    <section className="rounded-lg border bg-card" data-testid="dash-sales">
      <h2 className="sr-only">Takings</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x lg:divide-y-0 divide-border">
        <Kpi label="Net sales" value={inr(s.total_sales)} testId="dash-total-sales">
          <Delta now={s.total_sales || 0} before={s.prev?.total_sales} money />
        </Kpi>
        <Kpi label="Bills" value={s.orders ?? 0}>
          <Delta now={s.orders || 0} before={s.prev?.orders} />
        </Kpi>
        <Kpi label="Average bill" value={inr(s.avg_bill)}>
          <span className="text-meta text-muted-foreground">
            {s.not_paid ? `${inr(s.not_paid)} on khata` : "Nothing left on khata"}
          </span>
        </Kpi>
        <Kpi label="Guests" value={s.covers ?? 0}>
          <span className="text-meta text-muted-foreground tabular-nums">
            {s.covers ? `${inr(s.per_cover)} per guest` : "No guests recorded"}
          </span>
        </Kpi>
      </div>
      <div className="border-t px-loose py-base">
        {payments.length === 0 ? (
          <p className="text-meta text-muted-foreground">No payments taken in this period.</p>
        ) : (
          <>
            <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted" role="img"
                 aria-label={payments.map(p => `${p.mode} ${p.pct}%`).join(", ")}>
              {payments.map((p, i) => (
                <div key={p.mode} style={{ width: `${p.pct}%`, background: colors[i] }} className="h-full min-w-[2px]" />
              ))}
            </div>
            <ul className="mt-snug flex flex-wrap gap-x-section gap-y-tight">
              {payments.map((p, i) => (
                <li key={p.mode} className="flex items-center gap-tight text-body">
                  <span className="h-2 w-2 rounded-full" style={{ background: colors[i] }} aria-hidden />
                  <span className="font-medium">{p.mode}</span>
                  <span className="tabular-nums">{inr(p.amount)}</span>
                  <span className="text-meta text-muted-foreground tabular-nums">{p.pct}%</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * What is on the floor right now and not yet in Net sales: running tables (not
 * billed). Printed bills not yet settled are IN net sales since 2026-10-30, as their
 * own "Unsettled" line of the payment split (owner: "add unsettled bill amount to
 * sale instead of separating them") — never as cash, so the drawer stays true.
 */
export function LiveStrip({ live, netSales, today = false }) {
  if (!live || live.error) {
    return live?.error ? (
      <p className="text-meta text-danger" role="alert" data-testid="dash-live-error">Running tables total not available — {live.error}</p>
    ) : null;
  }
  const running = live.running || {};
  const expected = (netSales || 0) + (running.amount || 0);
  const orders = n => `${n} ${n === 1 ? "order" : "orders"}`;
  return (
    <section className="rounded-lg border bg-card" data-testid="dash-live">
      <h2 className="px-loose pt-base text-meta font-medium text-muted-foreground">Right now — not yet in net sales</h2>
      <div className={`grid ${today ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"} sm:divide-x divide-y sm:divide-y-0 divide-border`}>
        <Kpi label="Running tables" value={inr(running.amount || 0)} testId="dash-live-running">
          <span className="text-meta text-muted-foreground">{running.orders ? `${orders(running.orders)} not billed · before tax` : "Nothing running"}</span>
        </Kpi>
        {/* Only for a one-day view of today: over a wider range "today" would add
            the live figures to the whole range's sales. Running is before tax, so the
            final bills will come to more than this. */}
        {today && (
          <Kpi label="Expected today" value={inr(expected)} testId="dash-live-expected">
            <span className="text-meta text-muted-foreground">Net sales + running (running before tax)</span>
          </Kpi>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 2. How the day moved
// ---------------------------------------------------------------------------

const bucketLabel = (bucket, singleDay) => {
  if (!bucket) return "";
  if (singleDay) {
    const h = Number(bucket.slice(11, 13));
    return `${h % 12 || 12}${h < 12 ? "am" : "pm"}`;
  }
  const d = new Date(`${bucket.slice(0, 10)}T00:00:00`);
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-md border bg-card px-snug py-tight shadow-md text-meta">
      <div className="font-medium mb-hair">{label}</div>
      <div className="text-body font-semibold tabular-nums">{inr(row.revenue)}</div>
      <div className="tabular-nums text-muted-foreground">{row.successful} paid bill{row.successful === 1 ? "" : "s"}</div>
      {row.complimentary > 0 && <div className="tabular-nums text-warning">{row.complimentary} complimentary</div>}
      {row.cancelled > 0 && <div className="tabular-nums text-danger">{row.cancelled} cancelled</div>}
    </div>
  );
}

/** Every hour between the first and last bill, so a quiet afternoon shows as a
 *  gap in the bars rather than vanishing from the axis. */
export function fillHours(series) {
  if (series.length < 2) return series;
  const byHour = new Map(series.map(b => [Number(b.bucket.slice(11, 13)), b]));
  const day = series[0].bucket.slice(0, 11);
  const first = Number(series[0].bucket.slice(11, 13));
  const span = (Number(series[series.length - 1].bucket.slice(11, 13)) - first + 24) % 24;
  const out = [];
  for (let i = 0; i <= span; i += 1) {
    const h = (first + i) % 24;
    out.push(byHour.get(h) || {
      bucket: `${day}${String(h).padStart(2, "0")}:00:00`, successful: 0, complimentary: 0, cancelled: 0, revenue: 0,
    });
  }
  return out;
}

/** Round axis steps (1, 2, 2.5 or 5 x 10^n), three to six of them, choosing the
 *  step whose top sits closest above the peak (owner, 2026-10-25: "why double,
 *  go to around 1.3x"). A 20,500 peak tops at 25k, a 35,384 peak at 40k. */
export function niceTicks(max) {
  if (!(max > 0)) return [0];
  const exp = Math.floor(Math.log10(max));
  let best = null;
  for (let e = exp - 2; e <= exp; e += 1) {
    for (const m of [1, 2, 2.5, 5]) {
      const step = m * 10 ** e;
      const n = Math.ceil(max / step);
      if (n < 3 || n > 6) continue;
      const top = n * step;
      if (!best || top < best.top || (top === best.top && step > best.step)) best = { step, top };
    }
  }
  const ticks = [];
  for (let k = 0; k * best.step <= best.top + 1e-9; k += 1) ticks.push(Math.round(k * best.step * 100) / 100);
  return ticks;
}

export function TrendPanel({ data, error, singleDay }) {
  const s = data || {};
  const raw = s.series || [];
  const series = (singleDay ? fillHours(raw) : raw).map(b => ({ ...b, label: bucketLabel(b.bucket, singleDay) }));
  const totals = series.reduce((acc, b) => ({
    comp: acc.comp + b.complimentary, cancelled: acc.cancelled + b.cancelled,
  }), { comp: 0, cancelled: 0 });
  const colors = useMemo(() => ({
    paid: tokenColor("primary"), grid: tokenColor("border"), axis: tokenColor("muted-foreground"),
  }), []);
  const compact = v => (v >= 1000 ? `${Math.round(v / 100) / 10}k` : String(v));
  const ticks = niceTicks(Math.max(0, ...series.map(b => b.revenue || 0)));
  return (
    <Panel
      title={singleDay ? "Sales through the day" : "Sales by day"}
      testId="dash-trend"
      error={error}
      loading={!data}
      className="lg:col-span-2"
      aside={data && (
        <div className="flex items-center gap-base text-meta text-muted-foreground tabular-nums">
          {totals.comp > 0 && <span className="text-warning">{totals.comp} complimentary</span>}
          <span className={totals.cancelled ? "text-danger" : ""}>{totals.cancelled} cancelled</span>
        </div>
      )}
    >
      {series.length === 0 ? (
        <p className="text-meta text-muted-foreground py-section text-center">No bills in this period.</p>
      ) : (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ top: 4, right: 0, bottom: 0, left: -8 }} barCategoryGap="22%">
              <CartesianGrid vertical={false} stroke={colors.grid} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} stroke={colors.axis} interval="preserveStartEnd" />
              <YAxis tickLine={false} axisLine={false} fontSize={11} stroke={colors.axis} tickFormatter={compact} width={44}
                     ticks={ticks} domain={[0, ticks[ticks.length - 1]]} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: colors.grid, opacity: 0.4 }} />
              {/* No grow-in animation: a figure the owner reads should simply be there. */}
              <Bar dataKey="revenue" fill={colors.paid} radius={[3, 3, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
}

export function OrderTypesPanel({ data, error, singleDay }) {
  const rows = (data || {}).by_type || [];
  const series = (data || {}).series || [];
  const busiest = series.reduce((best, b) => (b.revenue > (best?.revenue || 0) ? b : best), null);
  const largest = rows.reduce((m, r) => Math.max(m, r.max || 0), 0);
  return (
    <Panel title="By order type" testId="dash-types" error={error} loading={!data}
           bodyClass="px-loose pb-loose pt-snug flex flex-col flex-1">
      {rows.length === 0 && <p className="text-meta text-muted-foreground">No paid bills in this period.</p>}
      <div className="divide-y">
        {rows.map(r => (
          <div key={r.order_type} className="py-snug first:pt-0">
            <div className="flex items-baseline justify-between gap-snug">
              <span className="text-body font-semibold">{typeLabel(r.order_type)}</span>
              <span className="text-lede font-bold tabular-nums">{inr(r.total)}</span>
            </div>
            <dl className="mt-tight grid grid-cols-3 gap-x-snug gap-y-hair text-meta tabular-nums">
              <Fact label="Bills" value={r.orders} />
              <Fact label="Average" value={inr(r.avg)} />
              <Fact label="Largest" value={inr(r.max)} />
              <Fact label="Discount" value={inr(r.discount)} />
              <Fact label="Tax" value={inr(r.tax)} />
              <Fact label="Table time" value={r.avg_table_minutes == null ? "—" : `${r.avg_table_minutes} min`} />
            </dl>
          </div>
        ))}
      </div>
      {busiest && (
        <dl className="mt-auto grid grid-cols-2 gap-snug border-t pt-snug text-meta">
          <div>
            <dt className="text-muted-foreground">{singleDay ? "Busiest hour" : "Busiest day"}</dt>
            <dd className="text-body font-medium tabular-nums">
              {bucketLabel(busiest.bucket, singleDay)}, {inr(busiest.revenue)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Largest bill</dt>
            <dd className="text-body font-medium tabular-nums">{inr(largest)}</dd>
          </div>
        </dl>
      )}
    </Panel>
  );
}

function Fact({ label, value }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-body truncate">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. Leakage, items, outflow
// ---------------------------------------------------------------------------

const LEAK_GROUPS = [
  ["Kitchen tickets", [
    ["kot_not_billed", "Never billed", "Table cancelled after food was sent", Ban, true],
    ["kot_not_billed_day_change", "Closed by day change", "Still open when the day rolled over", MoonStar, true],
    ["kot_cancelled", "Items cancelled", "Cancelled after the kitchen had it", XCircle, true],
    ["kot_modified", "Items changed", "Quantity cut or note changed after KOT", PencilLine, true],
    ["kot_shifted", "Moved", "Tables or items moved", ArrowLeftRight, false],
  ]],
  ["Bills", [
    ["bill_modified", "Bills changed", "Reopened or payment type changed", FilePen, false],
    ["bill_reprinted", "Bills re-printed", "Amount is the bills' value, not a loss", Printer, false],
    ["bill_waived", "Given free", "Complimentary bills and no-charge items", Gift, true],
  ]],
];

export const LEAK_LABEL = Object.fromEntries(LEAK_GROUPS.flatMap(([, lines]) => lines.map(([k, label]) => [k, label])));

export function LeakagePanel({ data, error, onOpen }) {
  const lines = data || {};
  const lost = LEAK_GROUPS.flatMap(([, ls]) => ls).filter(l => l[4])
    .reduce((sum, [key]) => sum + (lines[key]?.amount || 0), 0);
  return (
    <Panel
      title="Leakage"
      testId="dash-leakage"
      error={error}
      loading={!data}
      bodyClass="pb-snug"
      aside={data && (
        <span className="text-meta text-muted-foreground">
          <span className={`text-body font-bold tabular-nums ${lost ? "text-danger" : "text-foreground"}`}>{inr(lost)}</span> lost or given away
        </span>
      )}
    >
      {LEAK_GROUPS.map(([group, items]) => (
        <div key={group} className="mt-snug">
          <div className="px-loose pb-hair text-meta font-medium text-muted-foreground">{group}</div>
          {items.map(([key, label, hint, Icon, isLoss]) => {
            const line = lines[key] || { count: 0, amount: 0 };
            const active = line.count > 0;
            return (
              <button key={key} type="button" data-testid={`leak-${key}`} onClick={() => onOpen(key)}
                      className="group w-full flex items-center gap-snug px-loose py-tight text-left hover:bg-muted/60 transition-colors">
                <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-md ${active && isLoss ? "bg-danger-subtle text-danger" : "bg-muted text-muted-foreground"}`}>
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className={`block text-body ${active ? "font-medium" : "text-muted-foreground"}`}>{label}</span>
                  <span className="block text-micro text-muted-foreground truncate">{hint}</span>
                </span>
                <span className="text-right tabular-nums">
                  <span className={`block text-body ${active ? "font-semibold" : "text-muted-foreground"}`}>{line.count}</span>
                  {line.amount ? <span className="block text-micro text-muted-foreground">{inr(line.amount)}</span> : null}
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden />
              </button>
            );
          })}
        </div>
      ))}
    </Panel>
  );
}

export function ItemsPanel({ data, error, itemsBy, onItemsBy }) {
  const [tab, setTab] = useState("top");
  const rows = (data || {})[tab] || [];
  const metric = r => (itemsBy === "revenue" ? r.revenue : r.qty);
  const peak = Math.max(1, ...rows.map(r => Number(metric(r)) || 0));
  const tabs = [["top", "Best sellers"], ["low", "Slow movers"]];
  return (
    <Panel
      title="Items"
      testId="dash-items"
      error={error}
      loading={!data}
      bodyClass="px-loose pb-loose"
      aside={<Segmented value={itemsBy} onValueChange={v => v && onItemsBy(v)}
                        options={[{ value: "qty", label: "Quantity" }, { value: "revenue", label: "Sales" }]} />}
    >
      <div role="tablist" className="flex gap-base border-b mb-snug">
        {tabs.map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                  className={`-mb-px border-b-2 pb-tight pt-snug text-body font-medium transition-colors ${tab === key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
      </div>
      {rows.length === 0 && <p className="text-meta text-muted-foreground">No items sold in this period.</p>}
      <ol className="space-y-hair">
        {rows.map((r, i) => {
          const value = Number(metric(r)) || 0;
          return (
            <li key={`${r.name}-${i}`} className="relative flex items-center gap-snug rounded-md px-snug py-tight overflow-hidden">
              <span className={`absolute inset-y-0 left-0 ${tab === "top" ? "bg-info-subtle" : "bg-muted"}`}
                    style={{ width: `${Math.max(value / peak, 0) * 100}%` }} aria-hidden />
              <span className="relative w-5 text-meta text-muted-foreground tabular-nums">{i + 1}</span>
              <span className="relative min-w-0 flex-1 truncate text-body">{r.name}</span>
              <span className="relative text-body font-medium tabular-nums whitespace-nowrap">
                {itemsBy === "revenue" ? inr(r.revenue) : r.qty}
              </span>
              <span className="relative w-20 text-right text-meta text-muted-foreground tabular-nums whitespace-nowrap">
                {itemsBy === "revenue" ? `${r.qty} sold` : inr(r.revenue)}
              </span>
            </li>
          );
        })}
      </ol>
    </Panel>
  );
}

export function OutflowPanel({ data, error, onOpen }) {
  const o = data || {};
  const parts = [
    ["expenses", "Expenses", o.expenses?.total || 0],
    ["withdrawals", "Withdrawals", o.withdrawals || 0],
  ];
  const peak = Math.max(1, ...parts.map(p => p[2]));
  return (
    <Panel title="Expenses & withdrawals" testId="dash-outflow" error={error} loading={!data} bodyClass="px-loose pb-loose">
      <div className="flex items-baseline gap-snug pt-tight">
        <span className="text-display font-bold tabular-nums" data-testid="dash-total-outflow">{inr(o.total_outflow)}</span>
        <span className="text-meta text-muted-foreground">went out of the drawer and the books</span>
      </div>
      <div className="mt-base space-y-snug">
        {parts.map(([kind, label, amount]) => (
          <button key={kind} type="button" data-testid={`outflow-${kind}`} onClick={() => onOpen(kind)}
                  className="group block w-full text-left">
            <span className="flex items-center justify-between text-body">
              <span className="font-medium">{label}</span>
              <span className="flex items-center gap-tight tabular-nums">
                {inr(amount)}
                <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden />
              </span>
            </span>
            <span className="mt-tight block h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <span className="block h-full rounded-full bg-danger/70" style={{ width: `${(amount / peak) * 100}%` }} />
            </span>
          </button>
        ))}
        {(o.expenses?.by_category || []).length > 0 && (
          <ul className="pl-snug border-l space-y-hair">
            {o.expenses.by_category.map(c => (
              <li key={c.category} className="flex justify-between text-meta tabular-nums">
                <span className="text-muted-foreground">{c.category}</span><span>{inr(c.amount)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <button type="button" data-testid="outflow-top_up" onClick={() => onOpen("top_up")}
              className="mt-base flex w-full items-center gap-snug rounded-md bg-muted/60 px-snug py-tight text-left hover:bg-muted transition-colors">
        <Wallet className="h-4 w-4 text-muted-foreground" aria-hidden />
        <span className="flex-1 text-body">Cash added to the drawer</span>
        <span className="text-body font-medium tabular-nums">{inr(o.cash_top_up)}</span>
      </button>
    </Panel>
  );
}
